import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Coins, Sparkles, Scissors, ChevronLeft, HelpCircle, Minus, Plus, X, AlertCircle, Crown, Star, Gem, Lock, Unlock, Ticket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../components/LoginModal';
import { toast } from 'sonner';
import { SEOHead } from '../components/SEOHead';
import { getLocalizedText } from '../components/MultiLanguageInput';
import Lottie from 'lottie-react';
import packImage from '../assets/pack-opening/pack-cardtopia.png';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tierConfig = {
  THE_ONE_ONLY: {
    label: 'THE ONE AND ONLY',
    gradient: 'from-amber-400 via-amber-500 to-amber-600',
    bgGradient: 'from-amber-600/20 to-amber-500/20',
    border: 'border-amber-500/50',
    progressBg: 'bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600',
    itemProgress: 'from-amber-500 to-amber-400',
    // Game style colors
    headerBg: 'linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #FF8C00 100%)',
    glowColor: 'rgba(236, 72, 153, 0.6)',
    iconColor: '#ec4899',
    starCount: 5
  },
  LEGEND: {
    label: 'LEGEND',
    gradient: 'from-yellow-400 to-yellow-600',
    bgGradient: 'from-yellow-600/20 to-orange-500/20',
    border: 'border-yellow-500/50',
    progressBg: 'bg-gradient-to-r from-yellow-400 to-yellow-600',
    itemProgress: 'from-yellow-400 to-yellow-600',
    headerBg: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
    glowColor: 'rgba(251, 191, 36, 0.5)',
    iconColor: '#fbbf24',
    starCount: 4
  },
  EPIC: {
    label: 'EPIC',
    gradient: 'from-amber-400 to-amber-600',
    bgGradient: 'from-amber-600/20 to-amber-800/20',
    border: 'border-amber-500/50',
    progressBg: 'bg-gradient-to-r from-amber-400 to-amber-600',
    itemProgress: 'from-amber-400 to-amber-600',
    headerBg: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 50%, #6d28d9 100%)',
    glowColor: 'rgba(168, 85, 247, 0.4)',
    iconColor: '#a855f7',
    starCount: 3
  },
  RARE: {
    label: 'RARE',
    gradient: 'from-blue-400 to-blue-600',
    bgGradient: 'from-blue-600/20 to-blue-800/20',
    border: 'border-blue-500/50',
    progressBg: 'bg-gradient-to-r from-blue-400 to-blue-600',
    itemProgress: 'from-blue-400 to-blue-600',
    headerBg: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
    glowColor: 'rgba(59, 130, 246, 0.4)',
    iconColor: '#3b82f6',
    starCount: 2
  }
};

// Modern Tier Header — Cardtopia
const TierHeader = ({ rarity, progress, itemCount, onInfoClick, t }) => {
  const config = tierConfig[rarity];
  const isTheOneOnly = rarity === 'THE_ONE_ONLY';
  const TierIcon = isTheOneOnly ? Crown : (rarity === 'LEGEND' ? Gem : Star);
  const tierLabel = t ? t(`rarity.${rarity}`) : config.label;

  // Circular progress geometry
  const R = 15.5;
  const CIRC = 2 * Math.PI * R;
  const dash = (Math.min(100, Math.max(0, progress)) / 100) * CIRC;

  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-[0_8px_24px_-8px_rgba(0,0,0,0.25)]"
      style={{ background: config.headerBg }}
      data-testid={`tier-header-${rarity}`}
    >
      {/* Sheen + depth overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-black/15 pointer-events-none" />
      <div
        className="absolute -right-6 -top-8 w-32 h-32 rounded-full blur-2xl opacity-40 pointer-events-none"
        style={{ background: config.glowColor }}
      />

      {/* Sparkles for THE ONE AND ONLY */}
      {isTheOneOnly && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-sparkle"
              style={{ left: `${12 + i * 14}%`, top: `${18 + (i % 3) * 26}%`, animationDelay: `${i * 0.35}s` }}
            >
              <Sparkles className="w-2.5 h-2.5 text-white/90" />
            </div>
          ))}
        </div>
      )}

      <div className="relative flex items-center gap-3 px-3.5 py-2.5">
        {/* Medallion */}
        <div className="relative shrink-0">
          <div className="w-11 h-11 rounded-xl bg-white/25 backdrop-blur-sm border border-white/50 flex items-center justify-center shadow-[inset_0_1px_2px_rgba(255,255,255,0.5)]">
            <TierIcon className="w-5 h-5 text-white" fill={isTheOneOnly || rarity === 'LEGEND' ? 'currentColor' : 'none'} strokeWidth={2} />
          </div>
          {(isTheOneOnly || rarity === 'LEGEND') && (
            <div className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white flex items-center justify-center shadow">
              <Star className="w-2.5 h-2.5" fill={config.iconColor} stroke="none" />
            </div>
          )}
        </div>

        {/* Label + stars */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="font-black text-white text-sm sm:text-base tracking-wider uppercase truncate"
              style={{ fontFamily: "'Chakra Petch', sans-serif", letterSpacing: '0.12em', textShadow: '0 2px 6px rgba(0,0,0,0.4)' }}
            >
              {tierLabel}
            </span>
            <span className="hidden sm:inline-flex items-center text-[10px] font-bold text-white bg-black/25 px-2 py-0.5 rounded-full backdrop-blur-sm shrink-0">
              {itemCount} {itemCount === 1 ? 'ITEM' : 'ITEMS'}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-1">
            {[...Array(config.starCount)].map((_, i) => (
              <Star
                key={i}
                className={`w-3 h-3 text-white ${isTheOneOnly ? 'animate-star-twinkle' : ''}`}
                fill="currentColor"
                stroke="none"
                style={{ filter: `drop-shadow(0 0 3px ${config.glowColor})`, animationDelay: isTheOneOnly ? `${i * 0.2}s` : undefined }}
              />
            ))}
            <span className="sm:hidden ml-1 text-[10px] font-bold text-white/90">{itemCount} {itemCount === 1 ? 'ITEM' : 'ITEMS'}</span>
          </div>
        </div>

        {/* Circular progress + info */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative w-11 h-11">
            <svg viewBox="0 0 36 36" className="w-11 h-11 -rotate-90">
              <circle cx="18" cy="18" r={R} fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth="3.5" />
              <circle
                cx="18" cy="18" r={R} fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round"
                strokeDasharray={`${dash} ${CIRC}`}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-extrabold text-white">
              {Math.round(progress)}%
            </span>
          </div>
          {isTheOneOnly && (
            <button
              onClick={onInfoClick}
              className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/35 flex items-center justify-center transition-all hover:scale-110 shadow"
              data-testid="the-one-only-info-btn"
            >
              <span className="text-white font-bold text-xs">?</span>
            </button>
          )}
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes star-twinkle { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.55;transform:scale(0.82);} }
        @keyframes sparkle { 0%,100%{opacity:0;transform:scale(0) rotate(0deg);} 50%{opacity:1;transform:scale(1) rotate(180deg);} }
        .animate-star-twinkle { animation: star-twinkle 1.6s ease-in-out infinite; }
        .animate-sparkle { animation: sparkle 2.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

const PACK_IMAGE = packImage;

const rarityGlow = {
  THE_ONE_ONLY: 'shadow-[0_0_40px_rgba(236,72,153,0.6)]',
  LEGEND: 'shadow-[0_0_40px_rgba(234,179,8,0.6)]',
  EPIC: 'shadow-[0_0_40px_rgba(168,85,247,0.6)]',
  RARE: 'shadow-[0_0_30px_rgba(59,130,246,0.5)]'
};

export const PoolDetail = () => {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const { user, token, refreshUser } = useAuth();
  const { openLogin } = useLoginModal();
  
  const [pool, setPool] = useState(null);
  const [items, setItems] = useState([]);
  const [poolStatus, setPoolStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pulling, setPulling] = useState(false);
  const [gachaStage, setGachaStage] = useState('idle');
  const [result, setResult] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isCut, setIsCut] = useState(false);
  const [showTheOneOnlyInfo, setShowTheOneOnlyInfo] = useState(false);
  const [selectedPoolItem, setSelectedPoolItem] = useState(null);
  const [showItemDetail, setShowItemDetail] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  
  // Multi-pull tracking
  const [currentPullIndex, setCurrentPullIndex] = useState(0);
  const [totalPulls, setTotalPulls] = useState(1);
  const [allResults, setAllResults] = useState([]);
  
  // Multi-result animation tracking
  const [revealingCardIndex, setRevealingCardIndex] = useState(-1);
  const [revealPhase, setRevealPhase] = useState('revealing'); // 'revealing' | 'spreading'
  
  // Pack animation states
  const [isPackItem, setIsPackItem] = useState(false);
  const [packCards, setPackCards] = useState([]);
  const [packRevealPhase, setPackRevealPhase] = useState('main'); // 'main' | 'cards'
  const [allPackCards, setAllPackCards] = useState([]); // All pack cards from all multi-pull results
  const [showPackReveal, setShowPackReveal] = useState(false); // Show pack reveal overlay in multi-result
  
  // Gadget category Lottie animation states
  const [gadgetLottieData, setGadgetLottieData] = useState(null);
  const [isLottiePlaying, setIsLottiePlaying] = useState(false);
  const lottieRef = useRef(null);
  
  // Check if pool is Gadget category
  const categoryName = getLocalizedText(pool?.category, 'en');
  const isGadgetCategory = pool?.category_id === 'gadget' || categoryName?.toLowerCase() === 'gadget';

  // Get background colors based on tier for result screen
  const getTierBackgroundColors = (rarity) => {
    switch (rarity) {
      case 'THE_ONE_ONLY':
        return {
          primary: '#4a1942', // Deep pink/magenta
          secondary: '#2d1030',
          glow: 'rgba(236, 72, 153, 0.3)'
        };
      case 'LEGEND':
        return {
          primary: '#4a3f00', // Deep gold/yellow
          secondary: '#2d2600',
          glow: 'rgba(234, 179, 8, 0.3)'
        };
      case 'EPIC':
        return {
          primary: '#2d1f4e', // Deep purple
          secondary: '#1a1230',
          glow: 'rgba(168, 85, 247, 0.3)'
        };
      case 'RARE':
        return {
          primary: '#1a2d4e', // Deep blue
          secondary: '#0f1a30',
          glow: 'rgba(59, 130, 246, 0.3)'
        };
      default:
        return {
          primary: '#FF8C00', // Default teal
          secondary: '#0c2235',
          glow: 'rgba(24, 191, 224, 0.3)'
        };
    }
  };

  // Get current result tier for background
  const currentResultTier = gachaStage === 'result' && result 
    ? result.item?.rarity 
    : gachaStage === 'multi-result' && allResults.length > 0 
      ? (revealingCardIndex >= 0 && revealingCardIndex < allResults.length 
          ? allResults[revealingCardIndex]?.item?.rarity 
          : allResults[0]?.item?.rarity)
      : null;
  
  const bgColors = currentResultTier 
    ? getTierBackgroundColors(currentResultTier) 
    : { primary: '#FF8C00', secondary: '#0c2235', glow: 'rgba(24, 191, 224, 0.3)' };

  useEffect(() => {
    fetchPool();
  }, [poolId]);

  // Load Lottie animation for Gadget category
  useEffect(() => {
    if (isGadgetCategory && !gadgetLottieData) {
      fetch('/animations/package_opening.json')
        .then(res => res.json())
        .then(data => setGadgetLottieData(data))
        .catch(err => console.error('Failed to load Lottie:', err));
    }
  }, [isGadgetCategory, gadgetLottieData]);

  // Multi-result card reveal animation sequence - 2 seconds per card
  useEffect(() => {
    if (gachaStage === 'multi-result' && allResults.length > 1) {
      setRevealPhase('revealing');
      setRevealingCardIndex(0);
      
      // Reveal cards one by one - 2 seconds per card
      const revealInterval = setInterval(() => {
        setRevealingCardIndex(prev => {
          if (prev >= allResults.length - 1) {
            clearInterval(revealInterval);
            // After all cards revealed, wait 1 second then start spreading phase
            setTimeout(() => {
              setRevealPhase('spreading');
            }, 1000);
            return prev;
          }
          return prev + 1;
        });
      }, 2000); // 2000ms = 2 seconds per card reveal
      
      return () => clearInterval(revealInterval);
    }
  }, [gachaStage, allResults.length]);

  const fetchPool = async () => {
    try {
      const [poolRes, itemsRes, statusRes] = await Promise.all([
        axios.get(`${API}/pools/${poolId}`),
        axios.get(`${API}/pools/${poolId}/items`),
        axios.get(`${API}/pools/${poolId}/status`)
      ]);
      setPool(poolRes.data);
      setItems(itemsRes.data);
      setPoolStatus(statusRes.data);
    } catch (err) {
      console.error('Failed to fetch pool:', err);
      toast.error(t('gacha.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Group items by rarity
  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.rarity]) {
      acc[item.rarity] = [];
    }
    acc[item.rarity].push(item);
    return acc;
  }, {});

  // Calculate tier progress
  const getTierProgress = (tierItems) => {
    const total = tierItems.reduce((sum, item) => sum + item.quantity, 0);
    const remaining = tierItems.reduce((sum, item) => sum + item.remaining, 0);
    return total > 0 ? (remaining / total) * 100 : 0;
  };

  const handlePull = async () => {
    // Check if user is logged in
    if (!user) {
      toast.error(t('auth.pleaseLogin', { defaultValue: 'Please login first' }));
      openLogin();
      return;
    }
    
    if (user.coins < pool.price * quantity) {
      toast.error(t('pools.insufficientCoins'));
      return;
    }

    // Show confirmation popup instead of starting gacha directly
    setShowConfirmation(true);
  };

  const startGacha = async () => {
    setShowConfirmation(false);
    setPulling(true);
    setSwipeProgress(0);
    setIsCut(false);
    
    // Initialize multi-pull tracking
    setCurrentPullIndex(0);
    setTotalPulls(quantity);
    setAllResults([]);
    
    // Use normal animation flow for all quantities
    // For Gadget category, use Lottie; for others, use swipe cut
    if (isGadgetCategory) {
      setGachaStage('gadget-ready');
    } else {
      setGachaStage('shaking');
    }
  };

  // Handle Gadget box click - play Lottie and reveal result
  const handleGadgetBoxClick = async () => {
    if (isLottiePlaying) return;
    
    setIsLottiePlaying(true);
    setGachaStage('gadget-opening');
    
    // Start Lottie animation and immediately fetch result (don't wait for animation)
    if (lottieRef.current) {
      lottieRef.current.goToAndPlay(0);
    }
    
    // Fetch all results for multi-pull
    try {
      const results = [];
      for (let i = 0; i < totalPulls; i++) {
        const res = await axios.post(
          `${API}/gacha/${poolId}/pull`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        results.push(res.data);
      }
      
      // Wait a short moment for box to start opening visually
      await new Promise(r => setTimeout(r, 800));
      
      setAllResults(results);
      
      // For multi-pull, show all results at once
      if (totalPulls > 1) {
        setGachaStage('multi-result');
      } else {
        setResult(results[0]);
        setGachaStage('result');
      }
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('gacha.pullFailed', { defaultValue: 'Pull failed' }));
      setGachaStage('idle');
    } finally {
      setIsLottiePlaying(false);
    }
  };

  // Handle Lottie animation complete (for non-immediate reveal)
  const handleLottieComplete = () => {
    // No longer used for Gadget - result shows immediately
  };
  
  // Continue to next pull in multi-pull
  const continueToNextPull = () => {
    const nextIndex = currentPullIndex + 1;
    setCurrentPullIndex(nextIndex);
    setResult(null);
    setSwipeProgress(0);
    setIsCut(false);
    
    if (isGadgetCategory) {
      setGachaStage('gadget-ready');
    } else {
      setGachaStage('shaking');
    }
  };
  
  // Check if there are more pulls remaining
  const hasMorePulls = currentPullIndex < totalPulls - 1;

  const handleSwipeCut = async () => {
    if (isCut) return;
    setIsCut(true);
    setGachaStage('cutting');
    
    await new Promise(r => setTimeout(r, 800));

    try {
      // Fetch all results for multi-pull
      const results = [];
      for (let i = 0; i < totalPulls; i++) {
        const res = await axios.post(
          `${API}/gacha/${poolId}/pull`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        results.push(res.data);
      }
      
      setAllResults(results);
      setGachaStage('revealing');
      await new Promise(r => setTimeout(r, 500));
      
      // Collect all pack cards from all results
      const collectedPackCards = [];
      results.forEach((res) => {
        if (res?.is_pack && res?.pack_cards?.length > 0) {
          res.pack_cards.forEach(card => {
            collectedPackCards.push({
              ...card,
              parent_item_name: res.item?.name,
              parent_rarity: res.item?.rarity
            });
          });
        }
      });
      setAllPackCards(collectedPackCards);

      if (totalPulls > 1) {
        // Multi-pull - always show multi-result grid (even if packs exist)
        setGachaStage('multi-result');
      } else {
        // Single pull
        const firstResult = results[0];
        if (firstResult?.is_pack && firstResult?.pack_cards?.length > 0) {
          // Pack item - special animation
          setIsPackItem(true);
          setPackCards(firstResult.pack_cards);
          setPackRevealPhase('main');
          setResult(firstResult);
          setGachaStage('pack-result');
          
          // Wait 2 seconds then reveal cards
          await new Promise(r => setTimeout(r, 2000));
          setPackRevealPhase('cards');
        } else {
          // Normal single pull
          setResult(firstResult);
          setGachaStage('result');
        }
      }
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('gacha.pullFailed', { defaultValue: 'Pull failed' }));
      setGachaStage('idle');
    }
  };

  const handleDrag = (event, info) => {
    // Calculate swipe progress based on horizontal drag (only positive/right direction)
    const progress = Math.min(Math.max(info.offset.x / 180, 0), 1);
    setSwipeProgress(progress);
  };

  const handleDragEnd = (event, info) => {
    // If swiped more than 150px to the right, trigger cut
    if (info.offset.x > 150 && !isCut) {
      setSwipeProgress(1);
      handleSwipeCut();
    } else {
      setSwipeProgress(0);
    }
  };

  const resetGacha = () => {
    setGachaStage('idle');
    setResult(null);
    setSwipeProgress(0);
    setIsCut(false);
    setPulling(false);
    setCurrentPullIndex(0);
    setTotalPulls(1);
    setAllResults([]);
    setQuantity(1);
    setRevealingCardIndex(-1);
    setRevealPhase('revealing');
    setIsPackItem(false);
    setPackCards([]);
    setPackRevealPhase('main');
    setAllPackCards([]);
    setShowPackReveal(false);
    fetchPool();
  };

  if (loading) {
    return (
      <div className="h-screen bg-[#FFFDF7] flex items-center justify-center">
        <div className="text-zinc-400 text-sm">{t('common.loading')}</div>
      </div>
    );
  }

  // Rarity order
  const rarityOrder = ['THE_ONE_ONLY', 'LEGEND', 'EPIC', 'RARE'];

  return (
    <div className="fixed inset-0 bg-[#FFFDF7] flex flex-col">
      {/* SEO for Pool - render when pool data is available */}
      {pool && <SEOHead pool={pool} />}
      
      <AnimatePresence mode="wait">
        {gachaStage === 'idle' ? (
          <motion.div
            key="idle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col h-full"
          >
            {/* Fixed Header */}
            <div className="flex-shrink-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#F0E3C8]">
              <div className="flex items-center justify-between px-4 lg:px-8 py-3 max-w-[1600px] mx-auto w-full">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-1.5 text-[#1A1206] hover:text-[#FF8C00] transition-colors"
                  data-testid="back-button"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="hidden sm:inline text-sm font-medium">{t('common.back', { defaultValue: 'Back' })}</span>
                </button>
                <span className="text-[#0c2235] font-bold text-xs sm:text-sm bg-[#FFC300] px-3 py-1 rounded-md truncate max-w-[50%]">
                  {getLocalizedText(pool?.name, currentLang)}
                </span>
                <button 
                  onClick={() => setShowHowToPlay(true)}
                  className="flex items-center gap-1.5 text-[#1A1206] hover:text-[#FF8C00] transition-colors"
                  data-testid="how-to-play-btn"
                >
                  <HelpCircle className="w-5 h-5" />
                  <span className="hidden sm:inline text-sm font-medium">How to Play</span>
                </button>
              </div>
            </div>

            {/* Scrollable Content - Two Column Layout */}
            <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="max-w-[1600px] mx-auto w-full px-4 lg:px-8 py-5 lg:py-8 pb-28 lg:pb-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                  {/* LEFT COLUMN - Thumbnail + Info + Buy */}
                  <div className="lg:col-span-4">
                    <div className="lg:sticky lg:top-6 space-y-4">
                      {/* Thumbnail */}
                      <div className="relative rounded-2xl overflow-hidden border border-[#FFC300]/30 shadow-[0_0_40px_rgba(113,223,231,0.25)]">
                        <img 
                          src={pool?.image_url} 
                          alt={getLocalizedText(pool?.name, currentLang)}
                          className="w-full aspect-[4/5] object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0c2235] via-transparent to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h1 className="text-2xl font-bold text-white leading-tight" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
                            {getLocalizedText(pool?.name, currentLang)}
                          </h1>
                          <p className="text-zinc-300 text-xs mt-1">
                            Start date: {pool?.created_at ? new Date(pool.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                          </p>
                        </div>
                      </div>

                      {/* Info Card */}
                      <div className="bg-white rounded-2xl border border-[#F0E3C8] p-4 space-y-3">
                        {/* Stock Progress */}
                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <span className="text-[#FF6A00]">🔥</span>
                            <span className="text-[#FF6A00] text-sm font-semibold">
                              {t('pools.remaining')} {items.reduce((sum, item) => sum + (item.remaining || 0), 0).toLocaleString()}
                            </span>
                            <span className="text-[#9A8F79] text-sm">
                              / {items.reduce((sum, item) => sum + (item.quantity || 0), 0).toLocaleString()}
                            </span>
                          </div>
                          <div className="h-2.5 bg-[#FFF3D6] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500 rounded-full transition-all"
                              style={{ 
                                width: `${items.reduce((sum, item) => sum + (item.quantity || 0), 0) > 0 
                                  ? (items.reduce((sum, item) => sum + (item.remaining || 0), 0) / items.reduce((sum, item) => sum + (item.quantity || 0), 0)) * 100 
                                  : 0}%` 
                              }}
                            />
                          </div>
                        </div>

                        {/* Price */}
                        <div className="flex items-center justify-between border-t border-[#F0E3C8] pt-3">
                          <span className="text-[#6E6452] text-sm">{t('pools.pricePerPack', { defaultValue: 'Price per pack' })}</span>
                          <div className="flex items-center gap-1.5">
                            <Coins className="w-5 h-5 text-[#FFB300]" />
                            <span className="text-[#1A1206] font-bold text-lg">{pool?.price}</span>
                          </div>
                        </div>

                        {/* Description */}
                        {getLocalizedText(pool?.description, currentLang) && (
                          <p className="text-[#6E6452] text-sm leading-relaxed border-t border-[#F0E3C8] pt-3">
                            {getLocalizedText(pool?.description, currentLang)}
                          </p>
                        )}
                      </div>

                    </div>
                  </div>

                  {/* RIGHT COLUMN - Items List by Tier */}
                  <div className="lg:col-span-8 space-y-6">
                {rarityOrder.map(rarity => {
                  const tierItems = groupedItems[rarity];
                  if (!tierItems || tierItems.length === 0) return null;
                  
                  const config = tierConfig[rarity];
                  const progress = getTierProgress(tierItems);

                  return (
                    <div key={rarity} className="space-y-3">
                      {/* Game-Style Tier Header */}
                      <TierHeader 
                        rarity={rarity} 
                        progress={progress} 
                        itemCount={tierItems.length}
                        onInfoClick={() => rarity === 'THE_ONE_ONLY' && setShowTheOneOnlyInfo(true)}
                        t={t}
                      />

                      {/* Items in this tier - 2-column grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {tierItems.map(item => {
                          const isSoldOut = item.remaining <= 0;
                          const stockPercent = item.quantity > 0 ? (item.remaining / item.quantity) * 100 : 0;
                          const tierFrameClass = `tier-frame tier-frame-${rarity.toLowerCase()}`;
                          
                          // Calculate total NFT count and remaining from card_variants
                          const totalNftCount = item.card_variants?.reduce((sum, cv) => sum + (cv.nft_count || 0), 0) || 0;
                          const totalNftRemaining = item.card_variants?.reduce((sum, cv) => sum + (cv.nft_remaining || 0), 0) || 0;
                          const hasNft = totalNftCount > 0 || item.is_mintable;
                          
                          return (
                            <div
                              key={item.id}
                              onClick={() => {
                                setSelectedPoolItem({ ...item, rarity: rarity });
                                setShowItemDetail(true);
                              }}
                              className={`relative rounded-xl overflow-hidden bg-white border ${config.border} ${isSoldOut ? 'opacity-50' : ''} cursor-pointer hover:shadow-[0_6px_20px_-6px_rgba(255,140,0,0.4)] hover:-translate-y-0.5 active:scale-[0.99] transition-all shadow-sm`}
                            >
                              <div className="p-2.5 flex items-start gap-3">
                                {/* Item Image with Tier Frame */}
                                <div className={`${tierFrameClass} flex-shrink-0 relative`}>
                                  <div className="w-14 h-[72px] rounded-md overflow-hidden bg-[#0a1520]">
                                    <img 
                                      src={item.image_url} 
                                      alt={getLocalizedText(item.name, currentLang)}
                                      className={`w-full h-full object-cover ${isSoldOut ? 'grayscale' : ''}`}
                                    />
                                  </div>
                                  {/* NFT Badge on Image */}
                                  {hasNft && (
                                    <div className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full px-1.5 py-0.5 flex items-center gap-0.5 shadow-lg">
                                      <Gem className="w-2.5 h-2.5 text-white" />
                                      <span className="text-[8px] text-white font-bold">NFT</span>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Name + Progress + Stats */}
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-[#1A1206] font-semibold text-xs sm:text-sm leading-snug line-clamp-2 mb-1.5">{getLocalizedText(item.name, currentLang)}</h4>

                                  {/* Progress */}
                                  <div className="h-1 bg-[#F0E3C8] rounded-full overflow-hidden mb-2">
                                    <div 
                                      className={`h-full bg-gradient-to-r ${config.itemProgress} rounded-full transition-all`}
                                      style={{ width: `${stockPercent}%` }}
                                    />
                                  </div>

                                  {/* Stats chips */}
                                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[10px]">
                                    {/* Sell Price */}
                                    <div className="inline-flex items-center gap-1 bg-[#FFF3D6] text-[#B26A00] font-semibold px-1.5 py-0.5 rounded-md">
                                      <Coins className="w-2.5 h-2.5 text-[#FFB300]" />
                                      {item.sell_value.toLocaleString()}
                                    </div>
                                    {/* Tickets */}
                                    <div className="inline-flex items-center gap-1 bg-violet-50 text-violet-600 font-semibold px-1.5 py-0.5 rounded-md">
                                      <Ticket className="w-2.5 h-2.5 text-violet-500" />
                                      {item.ticket_reward || Math.floor(item.sell_value / 100)}
                                    </div>
                                    {/* Stock */}
                                    <div className="inline-flex items-center gap-1 text-[#6E6452] font-semibold">
                                      <span className="text-[#9A8F79] font-normal">{t('pools.stock')}</span>
                                      <span className="text-[#1A1206]">{item.remaining}/{item.quantity}</span>
                                    </div>
                                    {/* NFT Remaining */}
                                    {hasNft && (
                                      <div className="inline-flex items-center gap-1 text-purple-600 font-semibold">
                                        <Gem className="w-2.5 h-2.5 text-purple-500" />
                                        <span className={totalNftRemaining > 0 ? 'text-purple-600' : 'text-zinc-400'}>{totalNftRemaining}/{totalNftCount}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Sold Out Overlay */}
                              {isSoldOut && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                                  <span className="text-amber-400 text-xs font-bold uppercase tracking-wider">{t('pools.soldOut')}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Ticket Gacha Info */}
                <div className="bg-gradient-to-r from-violet-900/50 to-purple-900/30 border border-violet-500/30 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                      <Ticket className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-xs">{t('gacha.ticketReward', { defaultValue: 'Gacha Ticket Reward' })}</h4>
                      <p className="text-amber-300/70 text-[10px]">{t('gacha.redeemableAtShop', { defaultValue: 'Redeemable at TCG Shop' })}</p>
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <div className="text-[#7A5C00] text-[9px] space-y-0.5">
                  <p>• {t('gacha.eachPurchaseGetsRandom', { defaultValue: 'Each purchase gets 1 random item' })}</p>
                  <p>• {t('gacha.soldOutNotObtained', { defaultValue: 'Sold out items will not be obtained' })}</p>
                </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Bottom Action Bar (Cardtopia light) */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, type: 'spring', stiffness: 200, damping: 22 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-[#FFFDF7]/95 backdrop-blur-xl border-t border-[#F0E3C8] shadow-[0_-8px_30px_-10px_rgba(255,140,0,0.25)]"
            >
              <div className="max-w-[1600px] mx-auto w-full px-4 lg:px-8 py-3 flex items-center gap-3">
                {/* Price summary */}
                <div className="hidden sm:flex flex-col shrink-0">
                  <span className="text-[#9A8F79] text-[11px] font-medium">{t('pools.pricePerPack', { defaultValue: 'Price per pack' })}</span>
                  <div className="flex items-center gap-1">
                    <Coins className="w-4 h-4 text-[#FFB300]" />
                    <span className="text-[#1A1206] font-bold text-base tabular-nums">{pool?.price}</span>
                  </div>
                </div>

                {/* Quantity Selector */}
                <div className="flex items-center gap-1 shrink-0 bg-[#FFF3D6] rounded-full p-1 border border-[#FFC300]/40">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-8 h-8 rounded-full bg-white border border-[#FFC300]/30 flex items-center justify-center text-[#FF8C00] hover:bg-[#FFE9B0] active:scale-90 transition-all"
                    data-testid="qty-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-[#1A1206] font-extrabold text-base w-7 text-center tabular-nums">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-8 h-8 rounded-full bg-white border border-[#FFC300]/30 flex items-center justify-center text-[#FF8C00] hover:bg-[#FFE9B0] active:scale-90 transition-all"
                    data-testid="qty-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* PULL / Rip Button */}
                <motion.button
                  onClick={handlePull}
                  disabled={pulling || (user && user.coins < pool?.price * quantity)}
                  whileTap={{ scale: 0.98 }}
                  className="relative flex-1 h-12 rounded-full overflow-hidden font-extrabold text-white text-sm sm:text-base tracking-wide bg-gradient-to-r from-[#FFD400] via-[#FFB300] to-[#FF8C00] shadow-[0_6px_20px_-4px_rgba(255,140,0,0.6)] hover:shadow-[0_8px_26px_-4px_rgba(255,140,0,0.75)] disabled:opacity-60 disabled:saturate-50 flex items-center justify-center gap-1.5 transition-shadow"
                  data-testid="buy-now-btn"
                >
                  {/* Shimmer sweep */}
                  <motion.span
                    aria-hidden
                    className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/50 to-transparent skew-x-12"
                    animate={{ x: ['-150%', '350%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.6 }}
                  />
                  <Sparkles className="w-4 h-4 text-white shrink-0" />
                  <span className="relative">{pulling ? t('pools.pulling', { defaultValue: 'Pulling...' }) : (user ? t('pools.buyNow') : t('gacha.connectToPull', { defaultValue: 'Connect to Pull' }))}</span>
                  {user && (
                    <span className="relative flex items-center gap-0.5 bg-black/20 rounded-full px-2 py-0.5 ml-0.5">
                      <Coins className="w-3.5 h-3.5 text-yellow-100" />
                      <span className="tabular-nums">{pool?.price * quantity}</span>
                    </span>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="gacha"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col items-center justify-center px-5 overflow-hidden relative"
          >
            {/* Animated Background - Dynamic based on result tier */}
            <motion.div 
              className="absolute inset-0 overflow-hidden"
              animate={{
                backgroundColor: bgColors.primary
              }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            >
              {/* Base gradient */}
              <motion.div 
                className="absolute inset-0"
                animate={{
                  background: `radial-gradient(ellipse at center, ${bgColors.primary} 0%, ${bgColors.primary} 50%, ${bgColors.secondary} 100%)`
                }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
              />
              
              {/* Animated gradient orbs - Color changes with tier */}
              <motion.div
                className="absolute w-96 h-96 rounded-full opacity-30"
                style={{
                  filter: 'blur(60px)',
                }}
                animate={{
                  x: [-100, 100, -100],
                  y: [-50, 50, -50],
                  scale: [1, 1.2, 1],
                  background: `radial-gradient(circle, ${bgColors.glow.replace('0.3', '1')} 0%, transparent 70%)`,
                }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute right-0 bottom-0 w-80 h-80 rounded-full opacity-30"
                style={{
                  filter: 'blur(60px)',
                }}
                animate={{
                  x: [50, -50, 50],
                  y: [50, -50, 50],
                  scale: [1.2, 1, 1.2],
                  background: currentResultTier 
                    ? `radial-gradient(circle, ${bgColors.glow.replace('0.3', '0.8')} 0%, transparent 70%)`
                    : 'radial-gradient(circle, #f97316 0%, transparent 70%)',
                }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute left-1/2 top-1/2 w-64 h-64 rounded-full opacity-20"
                style={{
                  filter: 'blur(40px)',
                  transform: 'translate(-50%, -50%)',
                }}
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.2, 0.4, 0.2],
                  background: `radial-gradient(circle, ${bgColors.glow.replace('0.3', '1')} 0%, transparent 70%)`,
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />

              {/* Floating particles */}
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                  animate={{
                    y: [0, -30, 0],
                    opacity: [0, 0.8, 0],
                    scale: [0, 1, 0],
                  }}
                  transition={{
                    duration: 3 + Math.random() * 2,
                    repeat: Infinity,
                    delay: Math.random() * 3,
                    ease: "easeInOut",
                  }}
                />
              ))}

              {/* Grid lines effect */}
              <div 
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage: `
                    linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
                  `,
                  backgroundSize: '50px 50px',
                }}
              />
            </motion.div>

            {/* Gadget Category - Lottie Box Animation */}
            {isGadgetCategory && (gachaStage === 'gadget-ready' || gachaStage === 'gadget-opening') && gadgetLottieData && (
              <div className="relative flex flex-col items-center justify-center z-10 h-full">
                {/* Tap instruction */}
                {gachaStage === 'gadget-ready' && (
                  <motion.div
                    className="mb-6 text-center"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex items-center justify-center gap-2 text-[#1A1206] text-base font-extrabold mb-2 drop-shadow-[0_1px_2px_rgba(255,255,255,0.4)]">
                      <Sparkles className="w-5 h-5" />
                      <span>TAP TO OPEN!</span>
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <p className="text-[#3D2A0A] text-sm font-semibold">Tap the box to reveal your prize</p>
                  </motion.div>
                )}

                {/* Lottie Box Container */}
                <motion.div
                  className="relative cursor-pointer"
                  onClick={gachaStage === 'gadget-ready' ? handleGadgetBoxClick : undefined}
                  animate={gachaStage === 'gadget-ready' ? {
                    rotate: [-2, 2, -2],
                    scale: [1, 1.02, 1],
                  } : {}}
                  transition={{
                    repeat: Infinity,
                    duration: 0.5,
                    ease: "easeInOut"
                  }}
                  whileHover={gachaStage === 'gadget-ready' ? { scale: 1.05 } : {}}
                  whileTap={gachaStage === 'gadget-ready' ? { scale: 0.95 } : {}}
                >
                  {/* Glow effect behind box */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-amber-500/30 via-amber-500/30 to-amber-500/30 blur-3xl rounded-full"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 2,
                    }}
                  />
                  
                  {/* Lottie Animation */}
                  <Lottie
                    lottieRef={lottieRef}
                    animationData={gadgetLottieData}
                    loop={false}
                    autoplay={false}
                    onComplete={handleLottieComplete}
                    style={{ width: 300, height: 300 }}
                  />

                  {/* Shine particles around box */}
                  {gachaStage === 'gadget-ready' && (
                    <>
                      {[...Array(8)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-2 h-2 bg-yellow-400 rounded-full"
                          style={{
                            top: '50%',
                            left: '50%',
                          }}
                          animate={{
                            x: [0, Math.cos(i * 45 * Math.PI / 180) * 120],
                            y: [0, Math.sin(i * 45 * Math.PI / 180) * 120],
                            opacity: [1, 0],
                            scale: [1, 0],
                          }}
                          transition={{
                            repeat: Infinity,
                            duration: 1.5,
                            delay: i * 0.1,
                            ease: "easeOut"
                          }}
                        />
                      ))}
                    </>
                  )}
                </motion.div>

                {/* Opening text */}
                {gachaStage === 'gadget-opening' && (
                  <motion.p
                    className="mt-4 text-white text-lg font-semibold"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ repeat: Infinity, duration: 0.8 }}
                  >
                    {t('gacha.opening', { defaultValue: 'Opening...' })}
                  </motion.p>
                )}
              </div>
            )}

            {/* Gacha Animation - Standard (Non-Gadget) - Swipe to Cut */}
            {!isGadgetCategory && (gachaStage === 'shaking' || gachaStage === 'cutting') && (
              <div className="relative flex flex-col items-center z-10">
                {/* Swipe instruction */}
                {gachaStage === 'shaking' && !isCut && (
                  <motion.div
                    className="mb-8 text-center"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="flex items-center justify-center gap-2 text-[#1A1206] text-base font-extrabold mb-2 drop-shadow-[0_1px_2px_rgba(255,255,255,0.4)]">
                      <Scissors className="w-5 h-5" />
                      <span>{t('gacha.swipeToCut', { defaultValue: 'SWIPE TO CUT' })}</span>
                      <Scissors className="w-5 h-5 transform scale-x-[-1]" />
                    </div>
                    <motion.div 
                      className="flex items-center justify-center gap-1 text-[#3D2A0A] text-sm font-semibold"
                      animate={{ x: [-10, 10, -10] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                    >
                      <span>←</span>
                      <span>{t('gacha.swipeOnCutLine', { defaultValue: 'Swipe on cut line' })}</span>
                      <span>→</span>
                    </motion.div>
                  </motion.div>
                )}

                {/* Pack Container */}
                <div className="relative">
                  <div className="relative w-56 h-72">
                    {/* Top Half of Pack */}
                    <motion.div
                      className="absolute top-0 left-0 right-0 h-1/2 overflow-hidden"
                      animate={gachaStage === 'cutting' ? { 
                        y: -80, 
                        rotateX: -30,
                        opacity: 0
                      } : {}}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                      <div className="w-56 h-72 overflow-hidden" style={{ marginTop: 0 }}>
                        <img src={PACK_IMAGE} alt="Pack" className="w-full h-full object-contain" />
                      </div>
                    </motion.div>

                    {/* Bottom Half of Pack */}
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-1/2 overflow-hidden"
                      animate={gachaStage === 'cutting' ? { 
                        y: 80, 
                        rotateX: 30,
                        opacity: 0
                      } : {}}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                      <div className="w-56 h-72 overflow-hidden" style={{ marginTop: '-144px' }}>
                        <img src={PACK_IMAGE} alt="Pack" className="w-full h-full object-contain" />
                      </div>
                    </motion.div>

                    {/* Full Pack (shown when not cut) */}
                    {gachaStage === 'shaking' && (
                      <motion.div
                        className="absolute inset-0 overflow-hidden"
                        animate={{
                          x: [-2, 2, -2, 2, 0],
                          rotate: [-0.5, 0.5, -0.5, 0.5, 0]
                        }}
                        transition={{ duration: 0.3, repeat: Infinity, repeatDelay: 0.5 }}
                      >
                        <img src={PACK_IMAGE} alt="Pack" className="w-full h-full object-contain" />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <motion.div
                            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.7, 0.3] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                          >
                            <Sparkles className="w-12 h-12 text-amber-400" />
                          </motion.div>
                        </div>
                      </motion.div>
                    )}

                    {/* Cut Line Area - Swipeable */}
                    {gachaStage === 'shaking' && !isCut && (
                      <motion.div
                        className="absolute left-0 right-0 h-24 cursor-grab active:cursor-grabbing touch-none z-10"
                        style={{ top: '50%', marginTop: '-48px' }}
                        drag="x"
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={0.3}
                        onDrag={handleDrag}
                        onDragEnd={handleDragEnd}
                        whileDrag={{ scale: 1.01 }}
                      >
                        {/* Dashed cut line guide */}
                        <div className="absolute top-1/2 left-0 right-0 h-0.5 -mt-0.5">
                          <svg width="100%" height="4" className="overflow-visible">
                            <line x1="0" y1="2" x2="100%" y2="2" stroke="rgba(255, 255, 255, 0.4)" strokeWidth="2" strokeDasharray="8 4" />
                          </svg>
                        </div>

                        {/* Cut effect area */}
                        {swipeProgress > 0 && (
                          <div className="absolute top-1/2 left-0 -translate-y-1/2 overflow-visible" style={{ width: `${swipeProgress * 100}%`, height: '40px' }}>
                            <div 
                              className="absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2"
                              style={{
                                background: 'linear-gradient(90deg, rgba(255,255,255,1), rgba(236, 72, 153, 0.9))',
                                boxShadow: '0 0 10px rgba(255,255,255,0.9), 0 0 20px rgba(236, 72, 153, 0.7)'
                              }}
                            />
                          </div>
                        )}

                        {/* Scissors icon */}
                        <motion.div
                          className="absolute top-1/2 -translate-y-1/2 pointer-events-none z-20"
                          style={{ left: `${Math.max(0, Math.min(95, swipeProgress * 100))}%`, transform: 'translateX(-50%) translateY(-50%)' }}
                        >
                          <motion.div
                            animate={{ rotate: swipeProgress > 0 ? [0, -5, 0, 5, 0] : 0, scale: swipeProgress > 0 ? [1, 1.05, 1] : 1 }}
                            transition={{ duration: 0.2, repeat: swipeProgress > 0 ? Infinity : 0 }}
                          >
                            <div className="bg-white p-2.5 rounded-full" style={{ boxShadow: '0 0 15px rgba(255,255,255,0.8)' }}>
                              <Scissors className="w-6 h-6 text-amber-500" />
                            </div>
                          </motion.div>
                        </motion.div>

                        {/* Hint at start */}
                        {swipeProgress === 0 && (
                          <motion.div
                            className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-white/70 text-xs"
                            animate={{ x: [0, 10, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                          >
                            <span>→</span>
                            <span>Geser</span>
                          </motion.div>
                        )}
                      </motion.div>
                    )}

                    {/* Light burst effect when cutting */}
                    {gachaStage === 'cutting' && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-amber-500/50 via-white to-amber-400/50"
                        initial={{ opacity: 0, scale: 1 }}
                        animate={{ opacity: [0, 0.8, 0], scale: [1, 1.1, 1.3] }}
                        transition={{ duration: 0.5 }}
                      />
                    )}
                  </div>
                </div>

                {/* Progress bar */}
                {gachaStage === 'shaking' && !isCut && (
                  <div className="mt-8 w-56">
                    <div className="h-2.5 bg-black/15 rounded-full overflow-hidden backdrop-blur-sm">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-amber-500 via-orange-500 to-[#B24500] rounded-full"
                        style={{ width: `${swipeProgress * 100}%` }}
                      />
                    </div>
                    <p className="text-center text-[#1A1206] text-xs font-bold mt-3 drop-shadow-[0_1px_2px_rgba(255,255,255,0.4)]">
                      {swipeProgress > 0 
                        ? swipeProgress >= 0.7 
                          ? t('gacha.almostOpen', { defaultValue: 'Almost open!' })
                          : `${t('gacha.cutting', { defaultValue: 'Cutting...' })} ${Math.round(swipeProgress * 100)}%`
                        : t('gacha.slideToOpen', { defaultValue: 'Slide the cut line to open' })}
                    </p>
                  </div>
                )}

                {/* Cutting text */}
                {gachaStage === 'cutting' && (
                  <motion.p
                    className="mt-8 text-[#1A1206] font-extrabold text-base drop-shadow-[0_1px_2px_rgba(255,255,255,0.4)]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ duration: 0.6 }}
                  >
                    {t('gacha.opening', { defaultValue: 'Opening...' })}
                  </motion.p>
                )}
              </div>
            )}

            {(gachaStage === 'revealing' || gachaStage === 'gadget-reveal') && (
              <motion.div
                className="absolute inset-0 bg-white"
                initial={{ opacity: 1 }}
                animate={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              />
            )}

            {gachaStage === 'result' && result && (
              <motion.div
                className="text-center relative z-10 w-full max-w-sm mx-auto px-4"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              >
                {/* Special background for THE ONE ONLY */}
                {result.item.rarity === 'THE_ONE_ONLY' && (
                  <>
                    {/* Golden radial burst */}
                    <motion.div
                      className="absolute inset-0 -z-10"
                      style={{
                        background: 'radial-gradient(circle at center, rgba(255,215,0,0.4) 0%, rgba(255,165,0,0.2) 30%, transparent 70%)',
                      }}
                      animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    {/* Rotating gold rays */}
                    <motion.div
                      className="absolute inset-0 -z-10"
                      style={{
                        background: 'conic-gradient(from 0deg, transparent, rgba(255,215,0,0.3), transparent, rgba(255,215,0,0.3), transparent, rgba(255,215,0,0.3), transparent, rgba(255,215,0,0.3), transparent)',
                      }}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
                    />
                    {/* Sparkles */}
                    {[...Array(12)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute"
                        style={{
                          left: `${20 + Math.random() * 60}%`,
                          top: `${10 + Math.random() * 80}%`,
                        }}
                        animate={{
                          scale: [0, 1, 0],
                          opacity: [0, 1, 0],
                          rotate: [0, 180, 360],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          delay: i * 0.2,
                        }}
                      >
                        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FFD700">
                          <path d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10L12 0Z" />
                        </svg>
                      </motion.div>
                    ))}
                  </>
                )}

                {/* Special background for LEGEND */}
                {result.item.rarity === 'LEGEND' && (
                  <>
                    <motion.div
                      className="absolute inset-0 -z-10"
                      style={{
                        background: 'radial-gradient(circle at center, rgba(251,191,36,0.4) 0%, rgba(234,179,8,0.2) 40%, transparent 70%)',
                      }}
                      animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.9, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    {/* Rotating rays */}
                    <motion.div
                      className="absolute inset-0 -z-10"
                      style={{
                        background: 'conic-gradient(from 0deg, transparent, rgba(251,191,36,0.15), transparent, rgba(251,191,36,0.15), transparent)',
                      }}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
                    />
                    {[...Array(8)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1.5 h-1.5 bg-yellow-400 rounded-full"
                        style={{
                          left: `${20 + Math.random() * 60}%`,
                          top: `${15 + Math.random() * 70}%`,
                        }}
                        animate={{
                          y: [-20, -50],
                          opacity: [0, 1, 0],
                          scale: [0.5, 1.2, 0.5],
                        }}
                        transition={{
                          duration: 2.5,
                          repeat: Infinity,
                          delay: i * 0.3,
                        }}
                      />
                    ))}
                  </>
                )}

                {/* Background for EPIC */}
                {result.item.rarity === 'EPIC' && (
                  <>
                    <motion.div
                      className="absolute inset-0 -z-10"
                      style={{
                        background: 'radial-gradient(circle at center, rgba(168,85,247,0.4) 0%, rgba(139,92,246,0.2) 40%, transparent 70%)',
                      }}
                      animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0.8, 0.4] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-purple-400 rounded-full"
                        style={{
                          left: `${25 + Math.random() * 50}%`,
                          top: `${20 + Math.random() * 60}%`,
                        }}
                        animate={{
                          y: [-15, -35],
                          opacity: [0, 1, 0],
                          scale: [0.5, 1, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.3,
                        }}
                      />
                    ))}
                  </>
                )}

                {/* Background for RARE */}
                {result.item.rarity === 'RARE' && (
                  <>
                    <motion.div
                      className="absolute inset-0 -z-10"
                      style={{
                        background: 'radial-gradient(circle at center, rgba(59,130,246,0.35) 0%, rgba(37,99,235,0.15) 40%, transparent 70%)',
                      }}
                      animate={{ scale: [1, 1.08, 1], opacity: [0.3, 0.7, 0.3] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-blue-400 rounded-full"
                        style={{
                          left: `${28 + Math.random() * 44}%`,
                          top: `${25 + Math.random() * 50}%`,
                        }}
                        animate={{
                          y: [-10, -30],
                          opacity: [0, 1, 0],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.4,
                        }}
                      />
                    ))}
                  </>
                )}

                {/* Background for UNCOMMON */}
                {result.item.rarity === 'UNCOMMON' && (
                  <motion.div
                    className="absolute inset-0 -z-10"
                    style={{
                      background: 'radial-gradient(circle at center, rgba(34,197,94,0.3) 0%, rgba(22,163,74,0.1) 40%, transparent 70%)',
                    }}
                    animate={{ scale: [1, 1.05, 1], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                  />
                )}

                {/* Background for COMMON */}
                {result.item.rarity === 'COMMON' && (
                  <motion.div
                    className="absolute inset-0 -z-10"
                    style={{
                      background: 'radial-gradient(circle at center, rgba(156,163,175,0.25) 0%, rgba(107,114,128,0.1) 40%, transparent 70%)',
                    }}
                    animate={{ scale: [1, 1.03, 1], opacity: [0.2, 0.5, 0.2] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  />
                )}

                {/* Congratulations Header */}
                {result.item.rarity === 'THE_ONE_ONLY' ? (
                  <motion.div
                    className="mb-4 text-center"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    <motion.h2 
                      className="text-2xl font-black uppercase tracking-wider"
                      style={{
                        background: 'linear-gradient(90deg, #FFD700, #FFA500, #FFD700, #FFEC8B, #FFD700)',
                        backgroundSize: '200% auto',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textShadow: '0 0 20px rgba(255,215,0,0.5)',
                        fontFamily: "'Chakra Petch', sans-serif",
                      }}
                      animate={{ backgroundPosition: ['0% center', '200% center'] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    >
                      🏆 JACKPOT! 🏆
                    </motion.h2>
                    {/* Tier Badge */}
                    <motion.div 
                      className="inline-block px-4 py-1.5 rounded-lg text-sm font-bold uppercase mt-2 bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 text-black"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                    >
                      {t(`rarity.${result.item.rarity}`)}
                    </motion.div>
                    {/* Card Name */}
                    <h3 className="text-yellow-300 font-bold text-base mt-2 drop-shadow-lg">
                      {getLocalizedText(result.won_card_name, currentLang) || getLocalizedText(result.item.name, currentLang)}
                    </h3>
                  </motion.div>
                ) : result.item.rarity === 'LEGEND' ? (
                  <motion.div
                    className="mb-4 text-center"
                    animate={{ scale: [1, 1.03, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >
                    <h2 className="text-xl font-bold text-yellow-400 uppercase tracking-wider" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
                      🎉 CONGRATULATIONS! 🎉
                    </h2>
                    {/* Tier Badge */}
                    <div className={`inline-block px-4 py-1.5 rounded-lg text-sm font-bold uppercase mt-2 bg-gradient-to-r ${tierConfig[result.item.rarity]?.gradient || 'from-yellow-500 to-yellow-500'} text-white`}>
                      {t(`rarity.${result.item.rarity}`)}
                    </div>
                    {/* Card Name */}
                    <h3 className="text-white font-bold text-base mt-2 drop-shadow-lg">
                      {getLocalizedText(result.won_card_name, currentLang) || getLocalizedText(result.item.name, currentLang)}
                    </h3>
                  </motion.div>
                ) : (
                  <motion.div className="mb-4 text-center">
                    <h2 className="text-lg font-bold text-white uppercase tracking-wider" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
                      🎊 Congratulations! 🎊
                    </h2>
                    {/* Tier Badge */}
                    <div className={`inline-block px-4 py-1.5 rounded-lg text-sm font-bold uppercase mt-2 bg-gradient-to-r ${tierConfig[result.item.rarity]?.gradient || 'from-amber-500 to-amber-500'} text-white`}>
                      {t(`rarity.${result.item.rarity}`)}
                    </div>
                    {/* Card Name */}
                    <h3 className="text-white font-bold text-base mt-2 drop-shadow-lg">
                      {getLocalizedText(result.won_card_name, currentLang) || getLocalizedText(result.item.name, currentLang)}
                    </h3>
                  </motion.div>
                )}

                {/* Card Display - Bigger, Clean without text overlay */}
                <motion.div
                  className={`relative mx-auto ${result.item.rarity === 'THE_ONE_ONLY' ? 'w-52 h-72' : 'w-48 h-64'} rounded-2xl overflow-hidden`}
                  animate={result.item.rarity === 'THE_ONE_ONLY' 
                    ? { y: [0, -10, 0], rotateY: [0, 5, 0, -5, 0] } 
                    : result.item.rarity === 'LEGEND'
                    ? { y: [0, -8, 0] }
                    : { y: [0, -5, 0] }
                  }
                  transition={{ repeat: Infinity, duration: result.item.rarity === 'THE_ONE_ONLY' ? 3 : 2 }}
                  style={{
                    boxShadow: result.item.rarity === 'THE_ONE_ONLY'
                      ? '0 0 40px rgba(255,215,0,0.6), 0 0 80px rgba(255,165,0,0.4), 0 10px 40px rgba(0,0,0,0.5)'
                      : result.item.rarity === 'LEGEND'
                      ? '0 0 30px rgba(251,191,36,0.5), 0 10px 30px rgba(0,0,0,0.4)'
                      : '0 0 20px rgba(168,85,247,0.4), 0 10px 20px rgba(0,0,0,0.3)',
                  }}
                >
                  {/* Golden frame for THE ONE ONLY */}
                  {result.item.rarity === 'THE_ONE_ONLY' && (
                    <motion.div
                      className="absolute inset-0 z-10 pointer-events-none"
                      style={{
                        border: '4px solid',
                        borderImage: 'linear-gradient(45deg, #FFD700, #FFA500, #FFD700, #FFEC8B) 1',
                        borderRadius: '16px',
                      }}
                      animate={{
                        boxShadow: [
                          'inset 0 0 20px rgba(255,215,0,0.3)',
                          'inset 0 0 40px rgba(255,215,0,0.5)',
                          'inset 0 0 20px rgba(255,215,0,0.3)',
                        ],
                      }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                  )}

                  {/* Card Image - Show actual won card for LEGEND/EPIC/RARE variants */}
                  <img 
                    src={result.won_card_image || result.item.image_url}
                    alt={getLocalizedText(result.won_card_name, currentLang) || getLocalizedText(result.item.name, currentLang)}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Shine effect for THE ONE ONLY */}
                  {result.item.rarity === 'THE_ONE_ONLY' && (
                    <motion.div
                      className="absolute inset-0"
                      style={{
                        background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 45%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.4) 55%, transparent 60%)',
                        backgroundSize: '200% 100%',
                      }}
                      animate={{ backgroundPosition: ['-100% 0', '200% 0'] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                    />
                  )}
                  
                  {/* Shine effect for LEGEND */}
                  {result.item.rarity === 'LEGEND' && (
                    <motion.div
                      className="absolute inset-0"
                      style={{
                        background: 'linear-gradient(105deg, transparent 40%, rgba(255,215,0,0.2) 45%, rgba(255,215,0,0.4) 50%, rgba(255,215,0,0.2) 55%, transparent 60%)',
                        backgroundSize: '200% 100%',
                      }}
                      animate={{ backgroundPosition: ['-100% 0', '200% 0'] }}
                      transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 0.5 }}
                    />
                  )}
                </motion.div>

                {/* Item Value Info */}
                <div className="mt-4 flex justify-center gap-4 text-sm">
                  <div className="flex items-center gap-1 text-yellow-400">
                    <Coins className="w-4 h-4" />
                    <span className="font-semibold">{result.item.sell_value}</span>
                  </div>
                  <div className="flex items-center gap-1 text-violet-400">
                    <Ticket className="w-4 h-4" />
                    <span className="font-semibold">{result.item.ticket_reward || 0} {t('wallet.tickets')}</span>
                  </div>
                </div>

                {/* NFT Mintable Badge */}
                {result.item.is_mintable && (
                  <motion.div 
                    className="mt-3 flex justify-center"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="px-3 py-1.5 bg-gradient-to-r from-amber-600/20 to-amber-600/20 border border-amber-500/50 rounded-lg flex items-center gap-2">
                      <motion.div
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                      >
                        <Gem className="w-4 h-4 text-amber-400" />
                      </motion.div>
                      <span className="text-amber-300 text-xs font-semibold">{t('inventory.nftMintable', { defaultValue: 'NFT Mintable' })}</span>
                    </div>
                  </motion.div>
                )}

                {/* Multi-pull progress indicator */}
                {totalPulls > 1 && (
                  <div className="mt-3 text-center">
                    <p className="text-zinc-400 text-xs">
                      Item <span className="text-white font-bold">{currentPullIndex + 1}</span> of <span className="text-white font-bold">{totalPulls}</span>
                    </p>
                    <div className="mt-2 flex justify-center gap-1">
                      {[...Array(totalPulls)].map((_, i) => (
                        <div 
                          key={i}
                          className={`w-2 h-2 rounded-full ${i <= currentPullIndex ? 'bg-yellow-400' : 'bg-zinc-700'}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="mt-5 flex gap-3">
                  <Button
                    onClick={async () => {
                      try {
                        await axios.post(
                          `${API}/inventory/${result.inventory_id}/sell`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        toast.success(t('gacha.itemSold', { coins: result.item.sell_value }));
                        await refreshUser();
                        if (hasMorePulls) {
                          continueToNextPull();
                        } else {
                          resetGacha();
                        }
                      } catch (err) {
                        toast.error(t('gacha.sellFailed'));
                      }
                    }}
                    variant="outline"
                    className="flex-1 h-11 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 font-semibold text-sm rounded-xl"
                    data-testid="sell-btn"
                  >
                    <Coins className="w-4 h-4 mr-2" />
                    {t('inventory.sell', { defaultValue: 'Sell Back' })}
                  </Button>
                  <Button
                    onClick={() => {
                      if (hasMorePulls) {
                        continueToNextPull();
                      } else {
                        navigate('/inventory');
                      }
                    }}
                    className="flex-1 h-11 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold text-sm rounded-xl shadow-[0_0_20px_rgba(220,38,38,0.3)]"
                    data-testid="to-inventory-btn"
                  >
                    {hasMorePulls ? 'Next Item' : 'Go to Inventory'}
                  </Button>
                </div>

                {/* Continue to next / Keep Pulling Button */}
                {hasMorePulls ? (
                  <motion.div
                    className="mt-4"
                    animate={{ scale: [1, 1.02, 1] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    <Button
                      onClick={continueToNextPull}
                      className="w-full h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold text-base rounded-xl shadow-lg"
                      data-testid="continue-next-btn"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Continue to Open Next Item ({totalPulls - currentPullIndex - 1} remaining)
                    </Button>
                  </motion.div>
                ) : (
                  <Button
                    onClick={resetGacha}
                    variant="ghost"
                    className="mt-3 w-full h-10 text-zinc-400 hover:text-white hover:bg-white/10 font-medium text-sm"
                    data-testid="keep-pulling-btn"
                  >
                    Pull Again
                  </Button>
                )}
              </motion.div>
            )}

            {/* Pack Result Display - Show main item first, then cards spread */}
            {gachaStage === 'pack-result' && result && (
              <motion.div
                className="text-center relative z-10 w-full max-w-md mx-auto px-4 flex flex-col items-center justify-center h-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {/* Phase 1: Show Main Pack Item */}
                <AnimatePresence mode="wait">
                  {packRevealPhase === 'main' && (
                    <motion.div
                      key="pack-main"
                      className="text-center"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0.8, opacity: 0, y: -50 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    >
                      {/* Pack Title */}
                      <motion.div
                        className="mb-4"
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                      >
                        <h2 className="text-xl font-bold text-amber-400 uppercase tracking-wider" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
                          PACK OPENED!
                        </h2>
                        <div className={`inline-block px-4 py-1.5 rounded-lg text-sm font-bold uppercase mt-2 bg-gradient-to-r ${tierConfig[result.item.rarity]?.gradient || 'from-amber-500 to-amber-500'} text-white`}>
                          {packCards.length} Cards Inside
                        </div>
                      </motion.div>

                      {/* Main Pack Image */}
                      <motion.div
                        className="relative w-48 h-64 mx-auto rounded-2xl overflow-hidden"
                        animate={{ y: [0, -10, 0] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                        style={{
                          boxShadow: '0 0 30px rgba(6, 182, 212, 0.5), 0 10px 30px rgba(0,0,0,0.4)',
                        }}
                      >
                        <img
                          src={result.item.image_url}
                          alt={getLocalizedText(result.item.name, currentLang)}
                          className="w-full h-full object-cover"
                        />
                        {/* Glowing border */}
                        <motion.div
                          className="absolute inset-0 pointer-events-none rounded-2xl"
                          style={{ border: '3px solid rgba(6, 182, 212, 0.5)' }}
                          animate={{ boxShadow: ['inset 0 0 20px rgba(6,182,212,0.3)', 'inset 0 0 40px rgba(6,182,212,0.5)', 'inset 0 0 20px rgba(6,182,212,0.3)'] }}
                          transition={{ repeat: Infinity, duration: 1.5 }}
                        />
                      </motion.div>

                      {/* Pack Name */}
                      <h3 className="text-white font-bold text-lg mt-4 drop-shadow-lg">
                        {getLocalizedText(result.item.name, currentLang)}
                      </h3>

                      {/* Loading indicator */}
                      <motion.p
                        className="text-amber-400 text-sm mt-3"
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ repeat: Infinity, duration: 1 }}
                      >
                        Opening pack...
                      </motion.p>
                    </motion.div>
                  )}

                  {/* Phase 2: Show Cards Spread */}
                  {packRevealPhase === 'cards' && (
                    <motion.div
                      key="pack-cards"
                      className="w-full"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      {/* Header */}
                      <motion.div
                        className="mb-4 text-center"
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                      >
                        <h2 className="text-xl font-bold text-yellow-400 uppercase tracking-wider" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
                          YOU GOT {packCards.length} CARDS!
                        </h2>
                        <p className="text-zinc-400 text-sm mt-1">
                          From: {getLocalizedText(result.item.name, currentLang)}
                        </p>
                      </motion.div>

                      {/* Cards Grid - Spread Animation */}
                      <div className="grid grid-cols-3 gap-2 max-h-[50vh] overflow-y-auto py-2 px-1">
                        {packCards.map((card, index) => (
                          <motion.div
                            key={card.id}
                            className="relative aspect-[3/4] rounded-lg overflow-hidden bg-zinc-800"
                            initial={{ scale: 0, rotate: -30, opacity: 0 }}
                            animate={{ scale: 1, rotate: 0, opacity: 1 }}
                            transition={{ 
                              delay: index * 0.1, 
                              type: 'spring', 
                              stiffness: 300, 
                              damping: 20 
                            }}
                            style={{
                              boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                            }}
                          >
                            <img
                              src={card.image_url}
                              alt={typeof card.name === 'object' ? getLocalizedText(card.name, currentLang) : card.name}
                              className="w-full h-full object-cover"
                            />
                            {/* Card name overlay */}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1">
                              <p className="text-white text-[10px] font-medium truncate text-center">
                                {typeof card.name === 'object' ? getLocalizedText(card.name, currentLang) : card.name}
                              </p>
                            </div>
                            {/* NFT Badge */}
                            {card.is_mintable && (
                              <div className="absolute top-1 right-1 bg-amber-500/80 rounded px-1 py-0.5">
                                <span className="text-[8px] text-white font-bold">NFT</span>
                              </div>
                            )}
                          </motion.div>
                        ))}
                      </div>

                      {/* Action Buttons */}
                      <motion.div 
                        className="mt-4 flex flex-col gap-2"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: packCards.length * 0.1 + 0.3 }}
                      >
                        <Button
                          onClick={() => navigate('/inventory')}
                          className="w-full h-11 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold text-sm rounded-xl"
                          data-testid="pack-to-inventory-btn"
                        >
                          View in Collection
                        </Button>
                        <Button
                          onClick={resetGacha}
                          variant="ghost"
                          className="w-full h-10 text-zinc-400 hover:text-white hover:bg-white/10 font-medium text-sm"
                          data-testid="pack-pull-again-btn"
                        >
                          Pull Again
                        </Button>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Multi-Result Display - Dramatic Card Reveal Animation with Holo Effect */}
        {gachaStage === 'multi-result' && allResults.length > 0 && (
          <motion.div
            key="multi-result"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col h-full bg-gradient-to-b from-[#0c2235] via-[#0e2a45] to-[#0c2235] overflow-hidden relative"
          >
            {/* Holo Effect Styles */}
            <style>{`
              @keyframes holoSparkle {
                0%, 100% { opacity: .75; background-position: 50% 50%; filter: brightness(1.2) contrast(1.25); }
                5%, 8% { opacity: 1; background-position: 40% 40%; filter: brightness(.8) contrast(1.2); }
                13%, 16% { opacity: .5; background-position: 50% 50%; filter: brightness(1.2) contrast(.8); }
                35%, 38% { opacity: 1; background-position: 60% 60%; filter: brightness(1) contrast(1); }
                55% { opacity: .33; background-position: 45% 45%; filter: brightness(1.2) contrast(1.25); }
              }
              @keyframes holoGradient {
                0%, 100% { opacity: 0.5; background-position: 50% 50%; filter: brightness(.5) contrast(1); }
                5%, 9% { background-position: 100% 100%; opacity: 1; filter: brightness(.75) contrast(1.25); }
                13%, 17% { background-position: 0% 0%; opacity: .88; }
                35%, 39% { background-position: 100% 100%; opacity: 1; filter: brightness(.5) contrast(1); }
                55% { background-position: 0% 0%; opacity: 1; filter: brightness(.75) contrast(1.25); }
              }
              @keyframes holoCard {
                0%, 100% { transform: rotateZ(0deg) rotateX(0deg) rotateY(0deg); }
                5%, 8% { transform: rotateZ(0deg) rotateX(6deg) rotateY(-20deg); }
                13%, 16% { transform: rotateZ(0deg) rotateX(-9deg) rotateY(32deg); }
                35%, 38% { transform: rotateZ(3deg) rotateX(12deg) rotateY(20deg); }
                55% { transform: rotateZ(-3deg) rotateX(-12deg) rotateY(-27deg); }
              }
              .holo-card {
                animation: holoCard 6s ease 0s infinite;
              }
              .holo-card::before {
                content: "";
                position: absolute;
                left: 0; right: 0; bottom: 0; top: 0;
                background-position: 50% 50%;
                background-size: 300% 300%;
                background-image: linear-gradient(115deg, transparent 0%, var(--holo-color1) 25%, transparent 47%, transparent 53%, var(--holo-color2) 75%, transparent 100%);
                opacity: .5;
                filter: brightness(.5) contrast(1);
                z-index: 1;
                mix-blend-mode: color-dodge;
                animation: holoGradient 6s ease 0s infinite;
                border-radius: inherit;
              }
              .holo-card::after {
                content: "";
                position: absolute;
                left: 0; right: 0; bottom: 0; top: 0;
                background-image: url("https://assets.codepen.io/13471/sparkles.gif"),
                  url("https://assets.codepen.io/13471/holo.png"),
                  linear-gradient(125deg, #ff008450 15%, #fca40040 30%, #ffff0030 40%, #00ff8a20 60%, #00cfff40 70%, #cc4cfa50 85%);
                background-position: 50% 50%;
                background-size: 160%;
                background-blend-mode: overlay;
                z-index: 2;
                filter: brightness(1) contrast(1);
                mix-blend-mode: color-dodge;
                opacity: .75;
                animation: holoSparkle 6s ease 0s infinite;
                border-radius: inherit;
              }
            `}</style>

            {/* Animated Background Particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(30)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-amber-400/40 rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                  animate={{
                    y: [-20, -150],
                    x: [0, (Math.random() - 0.5) * 50],
                    opacity: [0, 1, 0],
                    scale: [0, 1.5, 0],
                  }}
                  transition={{
                    duration: 3 + Math.random() * 2,
                    repeat: Infinity,
                    delay: Math.random() * 3,
                  }}
                />
              ))}
            </div>

            {/* Revealing Phase - Cards come one by one, wait 2s, then stack behind */}
            {revealPhase === 'revealing' && (
              <div className="flex-1 flex flex-col items-center justify-center relative">
                {/* Stacked cards behind */}
                <div className="relative" style={{ perspective: '1200px', perspectiveOrigin: '50% 50%' }}>
                  {/* Background stack of revealed cards */}
                  {allResults.map((res, index) => {
                    const isRevealed = index < revealingCardIndex;
                    if (!isRevealed) return null;
                    
                    const stackPosition = revealingCardIndex - index - 1;
                    const offsetX = stackPosition * 8;
                    const offsetY = stackPosition * -5;
                    const scale = 0.9 - stackPosition * 0.05;
                    
                    return (
                      <motion.div
                        key={`stack-${index}`}
                        className="absolute left-1/2 top-1/2"
                        style={{ 
                          zIndex: index,
                          transformStyle: 'preserve-3d',
                        }}
                        initial={{ x: '-50%', y: '-50%', scale: 1, opacity: 1 }}
                        animate={{ 
                          x: `calc(-50% + ${offsetX}px)`,
                          y: `calc(-50% + ${offsetY}px)`,
                          scale: scale,
                          opacity: 0.6 - stackPosition * 0.1,
                          rotateY: stackPosition * 3,
                        }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                      >
                        <div 
                          className="w-48 h-72 rounded-2xl overflow-hidden border-2 relative"
                          style={{
                            borderColor: res.item.rarity === 'THE_ONE_ONLY' ? '#22d3d1' :
                              res.item.rarity === 'LEGEND' ? '#facc15' :
                              res.item.rarity === 'EPIC' ? '#a855f7' : '#3b82f6',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                          }}
                        >
                          <img 
                            src={res.won_card_image || res.item.image_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </motion.div>
                    );
                  })}
                  
                  {/* Current card being revealed with holo effect */}
                  {allResults[revealingCardIndex] && (
                    <motion.div
                      key={`current-${revealingCardIndex}`}
                      className="relative"
                      style={{ 
                        zIndex: 100,
                        transformStyle: 'preserve-3d',
                        '--holo-color1': allResults[revealingCardIndex].item.rarity === 'LEGEND' ? '#fac' : 
                          allResults[revealingCardIndex].item.rarity === 'EPIC' ? '#efb2fb' : 
                          allResults[revealingCardIndex].item.rarity === 'THE_ONE_ONLY' ? '#54a29e' : '#acc6f8',
                        '--holo-color2': allResults[revealingCardIndex].item.rarity === 'LEGEND' ? '#ddccaa' : 
                          allResults[revealingCardIndex].item.rarity === 'EPIC' ? '#acc6f8' : 
                          allResults[revealingCardIndex].item.rarity === 'THE_ONE_ONLY' ? '#a79d66' : '#efb2fb',
                      }}
                      initial={{ y: 500, scale: 0.3, opacity: 0, rotateX: 45 }}
                      animate={{ y: 0, scale: 1, opacity: 1, rotateX: 0 }}
                      transition={{ 
                        duration: 0.6, 
                        ease: [0.34, 1.56, 0.64, 1],
                      }}
                    >
                      <div 
                        className="holo-card w-52 h-80 rounded-2xl overflow-hidden border-3 relative"
                        style={{
                          borderColor: allResults[revealingCardIndex].item.rarity === 'THE_ONE_ONLY' ? '#22d3d1' :
                            allResults[revealingCardIndex].item.rarity === 'LEGEND' ? '#facc15' :
                            allResults[revealingCardIndex].item.rarity === 'EPIC' ? '#a855f7' : '#3b82f6',
                          boxShadow: allResults[revealingCardIndex].item.rarity === 'THE_ONE_ONLY' 
                            ? '-5px -5px 5px -5px #54a29e, 5px 5px 5px -5px #a79d66, 0 0 20px 4px rgba(255,255,255,0.4), 0 55px 35px -20px rgba(0,0,0,0.5)'
                            : allResults[revealingCardIndex].item.rarity === 'LEGEND'
                            ? '-5px -5px 5px -5px #fac, 5px 5px 5px -5px #ddccaa, 0 0 20px 4px rgba(255,255,255,0.4), 0 55px 35px -20px rgba(0,0,0,0.5)'
                            : allResults[revealingCardIndex].item.rarity === 'EPIC'
                            ? '-5px -5px 5px -5px #efb2fb, 5px 5px 5px -5px #acc6f8, 0 0 15px 4px rgba(255,255,255,0.3), 0 55px 35px -20px rgba(0,0,0,0.5)'
                            : '-5px -5px 5px -5px #acc6f8, 5px 5px 5px -5px #efb2fb, 0 0 10px 4px rgba(255,255,255,0.2), 0 55px 35px -20px rgba(0,0,0,0.5)',
                          isolation: 'isolate',
                        }}
                      >
                        <img 
                          src={allResults[revealingCardIndex].won_card_image || allResults[revealingCardIndex].item.image_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Rarity Badge */}
                        <div className={`absolute top-3 left-3 px-3 py-1 rounded-lg text-sm font-bold uppercase backdrop-blur-md z-10
                          ${allResults[revealingCardIndex].item.rarity === 'THE_ONE_ONLY' ? 'bg-amber-900/90 text-amber-300 shadow-amber-500/50' :
                            allResults[revealingCardIndex].item.rarity === 'LEGEND' ? 'bg-yellow-900/90 text-yellow-300 shadow-yellow-500/50' :
                            allResults[revealingCardIndex].item.rarity === 'EPIC' ? 'bg-purple-900/90 text-purple-300 shadow-purple-500/50' :
                            'bg-blue-900/90 text-blue-300 shadow-blue-500/50'}`}
                          style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}
                        >
                          {t(`rarity.${allResults[revealingCardIndex].item.rarity}`)}
                        </div>

                        {/* Card Name at bottom */}
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent z-10">
                          <p className="text-white text-sm font-bold text-center truncate">
                            {getLocalizedText(allResults[revealingCardIndex].won_card_name, currentLang) || 
                             getLocalizedText(allResults[revealingCardIndex].item.name, currentLang)}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
                
                {/* Counter */}
                <motion.div 
                  className="absolute bottom-8 text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <p className="text-amber-400 text-xl font-bold" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
                    {revealingCardIndex + 1} / {allResults.length}
                  </p>
                  <p className="text-zinc-500 text-sm mt-1">
                    {t('gacha.revealing', { defaultValue: 'Revealing cards...' })}
                  </p>
                </motion.div>
              </div>
            )}

            {/* Spreading Phase - All cards fan out with holo effect */}
            {revealPhase === 'spreading' && (
              <>
                {/* Header */}
                <motion.div 
                  className="flex-shrink-0 px-4 pt-4 pb-2 text-center"
                  initial={{ y: -30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                >
                  <h2 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
                    🎊 {t('gacha.youGot', { defaultValue: 'You Got' })} {allResults.length} {t('gacha.items', { defaultValue: 'Items' })}! 🎊
                  </h2>
                </motion.div>

                {/* Cards Grid with Holo Effect */}
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {allResults.map((res, index) => {
                      const holoColors = {
                        'THE_ONE_ONLY': { color1: '#54a29e', color2: '#a79d66' },
                        'LEGEND': { color1: '#fac', color2: '#ddccaa' },
                        'EPIC': { color1: '#efb2fb', color2: '#acc6f8' },
                        'RARE': { color1: '#acc6f8', color2: '#efb2fb' },
                      };
                      const rarityColors = {
                        'THE_ONE_ONLY': { border: '#22d3d1', glow: 'rgba(6,182,212,0.6)', text: 'text-amber-300', bg: 'bg-amber-900/80' },
                        'LEGEND': { border: '#facc15', glow: 'rgba(234,179,8,0.5)', text: 'text-yellow-300', bg: 'bg-yellow-900/80' },
                        'EPIC': { border: '#a855f7', glow: 'rgba(168,85,247,0.4)', text: 'text-purple-300', bg: 'bg-purple-900/80' },
                        'RARE': { border: '#3b82f6', glow: 'rgba(59,130,246,0.4)', text: 'text-blue-300', bg: 'bg-blue-900/80' },
                      };
                      const holo = holoColors[res.item.rarity] || holoColors['RARE'];
                      const colors = rarityColors[res.item.rarity] || rarityColors['RARE'];
                      
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, scale: 0.3, y: 150, rotateY: -30 }}
                          animate={{ opacity: 1, scale: 1, y: 0, rotateY: 0 }}
                          transition={{ 
                            delay: index * 0.1, 
                            duration: 0.6,
                            type: 'spring',
                            stiffness: 150,
                          }}
                          whileHover={{ scale: 1.08, y: -8 }}
                          className="holo-card relative rounded-xl overflow-hidden"
                          style={{ 
                            borderWidth: '2px',
                            borderColor: colors.border,
                            boxShadow: `-5px -5px 5px -5px ${holo.color1}, 5px 5px 5px -5px ${holo.color2}, 0 0 15px 2px rgba(255,255,255,0.2), 0 35px 25px -15px rgba(0,0,0,0.4)`,
                            '--holo-color1': holo.color1,
                            '--holo-color2': holo.color2,
                            isolation: 'isolate',
                            animationDelay: `${index * 0.2}s`,
                          }}
                        >
                          {/* Card Image */}
                          <div className="aspect-[2/3] relative overflow-hidden">
                            <img 
                              src={res.won_card_image || res.item.image_url}
                              alt={getLocalizedText(res.won_card_name, currentLang) || getLocalizedText(res.item.name, currentLang)}
                              className="w-full h-full object-cover"
                            />
                            
                            {/* Rarity Badge */}
                            <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${colors.bg} z-10`}>
                              <span className={colors.text}>{t(`rarity.${res.item.rarity}`)}</span>
                            </div>
                            
                            {/* Jackpot indicator */}
                            {res.is_jackpot && (
                              <motion.div 
                                className="absolute top-2 right-2 z-10"
                                animate={{ scale: [1, 1.3, 1], rotate: [0, 15, -15, 0] }}
                                transition={{ repeat: Infinity, duration: 1.5 }}
                              >
                                <span className="text-xl drop-shadow-lg">🏆</span>
                              </motion.div>
                            )}
                          </div>
                          
                          {/* Card Info */}
                          <div className="p-2 bg-gradient-to-t from-black/90 to-black/60">
                            <p className="text-white text-xs font-semibold truncate">
                              {getLocalizedText(res.won_card_name, currentLang) || getLocalizedText(res.item.name, currentLang)}
                            </p>
                            {res.is_pack && res.pack_cards?.length > 0 && (
                              <p className="text-amber-400 text-[10px] font-bold mt-0.5">
                                📦 Pack ({res.pack_cards.length} cards)
                              </p>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>

                {/* Bottom Actions */}
                <motion.div 
                  className="flex-shrink-0 p-4 bg-gradient-to-t from-[#0c2235] to-transparent"
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {/* Open All Packs Button - only show if there are pack results */}
                  {allPackCards.length > 0 && !showPackReveal && (
                    <Button
                      onClick={() => setShowPackReveal(true)}
                      className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold text-base rounded-xl shadow-lg mb-3"
                      data-testid="open-all-packs-btn"
                    >
                      📦 {t('gacha.openAllPacks', { defaultValue: 'Open All Packs' })} ({allPackCards.length} cards)
                    </Button>
                  )}
                  <div className="flex gap-3">
                    <Button
                      onClick={resetGacha}
                      className="flex-1 h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold text-base rounded-xl shadow-lg"
                      data-testid="pull-again-btn"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      {t('gacha.pullAgain', { defaultValue: 'Pull Again' })}
                    </Button>
                    <Button
                      onClick={() => navigate('/inventory')}
                      className="flex-1 h-12 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-bold text-base rounded-xl shadow-lg"
                      data-testid="multi-to-inventory-btn"
                    >
                      {t('gacha.goToInventory', { defaultValue: 'Go to Inventory' })}
                    </Button>
                  </div>
                </motion.div>

                {/* Pack Reveal Overlay */}
                <AnimatePresence>
                  {showPackReveal && allPackCards.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 z-50 bg-gradient-to-b from-[#1a0a2e] via-[#0e1a35] to-[#1a0a2e] flex flex-col overflow-hidden"
                    >
                      {/* Sparkle particles */}
                      <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        {[...Array(20)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-1.5 h-1.5 bg-amber-400/60 rounded-full"
                            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
                            animate={{ y: [-10, -120], opacity: [0, 1, 0], scale: [0, 1.5, 0] }}
                            transition={{ duration: 2.5 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
                          />
                        ))}
                      </div>

                      {/* Header */}
                      <motion.div
                        className="flex-shrink-0 px-4 pt-6 pb-2 text-center relative z-10"
                        initial={{ y: -30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                      >
                        <h2 className="text-2xl font-bold text-amber-400 mb-1" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
                          📦 Pack Cards
                        </h2>
                        <p className="text-zinc-400 text-sm">
                          {allPackCards.length} {t('gacha.cardsFromPacks', { defaultValue: 'cards from your packs' })}
                        </p>
                        <p className="text-emerald-400 text-xs mt-1">
                          {t('gacha.autoCollected', { defaultValue: 'All cards auto-added to your collection!' })}
                        </p>
                      </motion.div>

                      {/* Pack Cards Grid */}
                      <div className="flex-1 overflow-y-auto px-4 py-4 relative z-10">
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                          {allPackCards.map((card, index) => (
                            <motion.div
                              key={card.id || index}
                              initial={{ opacity: 0, scale: 0.3, rotateY: -90 }}
                              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                              transition={{
                                delay: index * 0.08,
                                duration: 0.5,
                                type: 'spring',
                                stiffness: 200,
                              }}
                              className="relative rounded-lg overflow-hidden border-2 border-amber-600/60"
                              style={{
                                boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3), 0 15px 20px -10px rgba(0,0,0,0.4)',
                              }}
                            >
                              <div className="aspect-[2/3] relative overflow-hidden bg-zinc-900">
                                <img
                                  src={card.image_url}
                                  alt={getLocalizedText(card.name, currentLang)}
                                  className="w-full h-full object-cover"
                                />
                                {card.is_mintable && (
                                  <div className="absolute top-1 right-1 bg-amber-500/90 px-1.5 py-0.5 rounded text-[8px] font-bold text-white">
                                    NFT
                                  </div>
                                )}
                              </div>
                              <div className="p-1.5 bg-gradient-to-t from-black/90 to-black/60">
                                <p className="text-white text-[10px] font-semibold truncate">
                                  {getLocalizedText(card.name, currentLang)}
                                </p>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>

                      {/* Close Button */}
                      <motion.div
                        className="flex-shrink-0 p-4 bg-gradient-to-t from-[#1a0a2e] to-transparent relative z-10"
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        <Button
                          onClick={() => setShowPackReveal(false)}
                          className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold text-base rounded-xl shadow-lg"
                          data-testid="close-pack-reveal-btn"
                        >
                          {t('common.close', { defaultValue: 'Close' })}
                        </Button>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Purchase Confirmation Bottom Sheet */}
      <AnimatePresence>
        {showConfirmation && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/60 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowConfirmation(false)}
            />

            {/* Bottom Sheet */}
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-50"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              <div className="bg-gradient-to-b from-[#0e2a45] to-[#0c2235] rounded-t-3xl border-t border-amber-500/30 p-5">
                {/* Handle */}
                <div className="w-12 h-1 bg-amber-500/50 rounded-full mx-auto mb-4" />

                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-base">Confirm Purchase</h3>
                  <button 
                    onClick={() => setShowConfirmation(false)}
                    className="w-8 h-8 rounded-full bg-[#0f2d4a] flex items-center justify-center text-zinc-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Pool Info */}
                <div className="flex gap-3 p-3 bg-amber-900/30 rounded-xl mb-4">
                  <img 
                    src={pool?.image_url} 
                    alt={getLocalizedText(pool?.name, currentLang)}
                    className="w-16 h-20 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <h4 className="text-white font-semibold text-sm mb-1">{getLocalizedText(pool?.name, currentLang)}</h4>
                    <p className="text-amber-300/70 text-xs mb-2">{getLocalizedText(pool?.description, currentLang)}</p>
                    <div className="flex items-center gap-1 text-yellow-400 text-sm font-bold">
                      <Coins className="w-4 h-4" />
                      <span>{pool?.price} / pack</span>
                    </div>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="bg-white/10 rounded-xl p-3 mb-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Quantity</span>
                    <span className="text-white font-medium">{quantity} pack</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Price per pack</span>
                    <span className="text-white font-medium">{pool?.price} coins</span>
                  </div>
                  <div className="border-t border-amber-900/40 pt-2 mt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-white font-semibold">Total</span>
                      <div className="flex items-center gap-1 text-yellow-400 font-bold">
                        <Coins className="w-4 h-4" />
                        <span>{pool?.price * quantity}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Balance Info */}
                <div className="flex items-center justify-between p-3 bg-white/10 rounded-xl mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <Coins className="w-4 h-4 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-zinc-400 text-xs">Your coin balance</p>
                      <p className="text-white font-bold text-sm">{user?.coins?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                  {user?.coins < pool?.price * quantity && (
                    <div className="flex items-center gap-1 text-amber-400 text-xs">
                      <AlertCircle className="w-3 h-3" />
                      <span>Insufficient balance</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowConfirmation(false)}
                    variant="outline"
                    className="flex-1 h-12 border-amber-500/50 text-white hover:bg-amber-900/50 font-semibold text-sm rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={startGacha}
                    disabled={!user || user.coins < pool?.price * quantity}
                    className="flex-1 h-12 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold text-sm rounded-xl disabled:opacity-50 shadow-[0_0_20px_rgba(220,38,38,0.3)]"
                    data-testid="confirm-buy-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Open Pack
                  </Button>
                </div>

                {/* Terms */}
                <p className="text-center text-zinc-500 text-[10px] mt-3">
                  By continuing, you agree to the applicable terms and conditions
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* THE ONE ONLY Info Bottom Sheet */}
      <AnimatePresence>
        {showTheOneOnlyInfo && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/70 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowTheOneOnlyInfo(false)}
            />

            {/* Bottom Sheet */}
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-50"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              <div className="bg-gradient-to-b from-[#0e2a45] to-[#0c2235] rounded-t-3xl border-t border-amber-500/30 overflow-hidden">
                {/* Animated Header */}
                <div 
                  className="relative py-4 px-5"
                  style={{
                    background: 'linear-gradient(90deg, #ec4899 0%, #a855f7 25%, #FF8C00 50%, #a855f7 75%, #ec4899 100%)',
                    backgroundSize: '300% 100%',
                    animation: 'gradient-flow 4s ease-in-out infinite'
                  }}
                >
                  <div className="absolute inset-0 bg-black/30" />
                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {/* Stars */}
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <svg 
                            key={i} 
                            viewBox="0 0 24 24" 
                            className="w-4 h-4 animate-pulse"
                            style={{ animationDelay: `${i * 0.1}s` }}
                          >
                            <path 
                              fill="#fff" 
                              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                            />
                          </svg>
                        ))}
                      </div>
                      <h3 className="text-white font-black text-lg tracking-wider" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
                        THE ONE AND ONLY
                      </h3>
                    </div>
                    <button 
                      onClick={() => setShowTheOneOnlyInfo(false)}
                      className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 space-y-4">
                  {/* Main Description */}
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                    <p className="text-white text-sm leading-relaxed">
                      <span className="text-amber-400 font-bold">THE ONE AND ONLY</span> {t('theOneOnly.description')} <span className="text-amber-400 font-bold">{t('theOneOnly.oneItem')}</span> {t('theOneOnly.inPool')}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="space-y-3">
                    <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-400" />
                      {t('theOneOnly.features')}
                    </h4>
                    
                    <div className="grid gap-2">
                      <div className="flex items-start gap-3 bg-white/10 rounded-lg p-3">
                        <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-amber-400 text-sm">1</span>
                        </div>
                        <div>
                          <p className="text-white text-xs font-medium">{t('theOneOnly.feature1Title')}</p>
                          <p className="text-zinc-400 text-[11px]">{t('theOneOnly.feature1Desc')}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 bg-white/10 rounded-lg p-3">
                        <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                          <Coins className="w-4 h-4 text-amber-400" />
                        </div>
                        <div>
                          <p className="text-white text-xs font-medium">{t('theOneOnly.feature2Title')}</p>
                          <p className="text-zinc-400 text-[11px]">{t('theOneOnly.feature2Desc')}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 bg-white/10 rounded-lg p-3">
                        <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                          <Crown className="w-4 h-4 text-amber-400" />
                        </div>
                        <div>
                          <p className="text-white text-xs font-medium">{t('theOneOnly.feature3Title')}</p>
                          <p className="text-zinc-400 text-[11px]">{t('theOneOnly.feature3Desc')}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Drop Rate Info */}
                  <div className="bg-gradient-to-r from-amber-500/10 to-amber-500/10 rounded-xl p-4 border border-amber-500/20">
                    <p className="text-zinc-300 text-xs text-center">
                      {t('theOneOnly.dropRateNote')}
                    </p>
                  </div>

                  {/* Close Button */}
                  <Button
                    onClick={() => setShowTheOneOnlyInfo(false)}
                    className="w-full h-11 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold rounded-xl shadow-[0_0_20px_rgba(220,38,38,0.3)]"
                  >
                    {t('theOneOnly.gotIt')}
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Item Detail Dialog */}
      <Dialog open={showItemDetail} onOpenChange={setShowItemDetail}>
        <DialogContent className="bg-white border-amber-500/30 max-w-xs p-0 overflow-hidden max-h-[85vh]">
          {selectedPoolItem && (() => {
            const config = tierConfig[selectedPoolItem.rarity];
            const isSoldOut = selectedPoolItem.remaining <= 0;
            const stockPercent = selectedPoolItem.quantity > 0 ? (selectedPoolItem.remaining / selectedPoolItem.quantity) * 100 : 0;
            
            const TierIcon = selectedPoolItem.rarity === 'THE_ONE_ONLY' ? Crown 
              : selectedPoolItem.rarity === 'LEGEND' ? Star 
              : selectedPoolItem.rarity === 'EPIC' ? Gem 
              : Sparkles;
            
            return (
              <div className="relative">
                {/* Close button */}
                <button
                  onClick={() => setShowItemDetail(false)}
                  className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>

                {/* Item Image - Full display with white background */}
                <div className="bg-white flex items-center justify-center p-2 min-h-[200px] relative">
                  <img
                    src={selectedPoolItem.image_url}
                    alt={getLocalizedText(selectedPoolItem.name, currentLang)}
                    className={`max-w-full max-h-[300px] object-contain ${isSoldOut ? 'grayscale' : ''}`}
                  />
                  
                  {/* Tier Badge */}
                  <div 
                    className="absolute top-2 left-2 px-2 py-1 rounded-md flex items-center gap-1 shadow-lg"
                    style={{ background: config?.headerBg }}
                  >
                    <TierIcon className="w-3 h-3 text-white" />
                    <span className="text-xs font-bold uppercase tracking-wider text-white">{config?.label}</span>
                  </div>

                  {/* Sold Out Overlay */}
                  {isSoldOut && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                      <span className="text-amber-400 text-xl font-bold uppercase tracking-wider">SOLD OUT</span>
                    </div>
                  )}
                </div>

                {/* Item Details */}
                <div className="p-3 space-y-3">
                  {/* Name */}
                  <h3 className="text-white text-base font-bold font-['Chakra_Petch'] leading-tight">
                    {getLocalizedText(selectedPoolItem.name, currentLang)}
                  </h3>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    {/* Sell Value */}
                    <div className="p-2 bg-amber-900/30 rounded-lg">
                      <p className="text-amber-300/60 text-[10px] uppercase tracking-wider">Harga Sell</p>
                      <p className="text-yellow-400 font-bold text-sm flex items-center gap-1">
                        <Coins className="w-3 h-3" />
                        {selectedPoolItem.sell_value?.toLocaleString() || 0}
                      </p>
                    </div>
                    
                    {/* Ticket Reward */}
                    <div className="p-2 bg-violet-900/30 rounded-lg">
                      <p className="text-violet-300/60 text-[10px] uppercase tracking-wider">Reward Tickets</p>
                      <p className="text-violet-400 font-bold text-sm flex items-center gap-1">
                        <Ticket className="w-3 h-3" />
                        {selectedPoolItem.ticket_reward || 0}
                      </p>
                    </div>
                  </div>

                  {/* Stock Progress */}
                  <div className="p-2 bg-amber-900/30 rounded-lg">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-amber-300/60 text-[10px] uppercase tracking-wider">Stock Available</p>
                      <p className="text-white font-bold text-sm">
                        {selectedPoolItem.remaining} / {selectedPoolItem.quantity}
                      </p>
                    </div>
                    <div className="h-2 bg-[#0f2d4a]/60 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${config?.itemProgress || 'from-amber-400 to-amber-600'} rounded-full transition-all`}
                        style={{ width: `${stockPercent}%` }}
                      />
                    </div>
                  </div>

                  {/* Drop Rate Info */}
                  <div className="flex items-center justify-between p-2 bg-white/10 rounded-lg">
                    <span className="text-amber-300/60 text-[10px] uppercase tracking-wider">Drop Rate</span>
                    <span className="text-white font-medium text-sm">
                      {selectedPoolItem.drop_rate?.toFixed(2) || 0}%
                    </span>
                  </div>

                  {/* Close Button */}
                  <Button
                    onClick={() => setShowItemDetail(false)}
                    className="w-full h-10 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-bold text-sm rounded-xl shadow-[0_0_20px_rgba(220,38,38,0.3)]"
                  >
                    Close
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* How to Play Modal */}
      <Dialog open={showHowToPlay} onOpenChange={setShowHowToPlay}>
        <DialogContent className="bg-white border-amber-900/40 max-w-md p-0 overflow-hidden max-h-[85vh]">
          <DialogHeader className="px-5 pt-5 pb-4 border-b border-amber-900/40">
            <DialogTitle className="text-white text-xl font-bold flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-yellow-500" />
              How to Play
            </DialogTitle>
          </DialogHeader>
          <div className="px-5 py-4 space-y-4 overflow-y-auto max-h-[65vh]">
            {/* Step 1 */}
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-yellow-500 font-bold text-sm">1</span>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Top Up Coins</h4>
                <p className="text-zinc-400 text-sm">Add coins to your wallet through the deposit feature. You'll need coins to pull from gacha pools.</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-yellow-500 font-bold text-sm">2</span>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Choose a Pool</h4>
                <p className="text-zinc-400 text-sm">Browse available pools and select one you're interested in. Each pool contains different collectible items.</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-yellow-500 font-bold text-sm">3</span>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Set Quantity & Pull</h4>
                <p className="text-zinc-400 text-sm">Select how many pulls you want (1-10), then tap the "PULL" button to start the gacha.</p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-yellow-500 font-bold text-sm">4</span>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Swipe to Open</h4>
                <p className="text-zinc-400 text-sm">After pulling, swipe right on the pack to open it and reveal your prize!</p>
              </div>
            </div>

            {/* Step 5 */}
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-yellow-500 font-bold text-sm">5</span>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Collect or Sell</h4>
                <p className="text-zinc-400 text-sm">Keep the item in your inventory, sell it back for coins, or request shipping for physical items.</p>
              </div>
            </div>

            {/* Rarity Info */}
            <div className="mt-6 p-4 bg-[#0f2d4a]/60 rounded-xl border border-amber-800/50">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                Rarity Tiers
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-amber-400 text-sm font-medium">THE ONE AND ONLY</span>
                  <span className="text-zinc-500 text-xs">Ultra Rare - 1 per pool</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-yellow-400 text-sm font-medium">LEGEND</span>
                  <span className="text-zinc-500 text-xs">Very Rare</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-amber-400 text-sm font-medium">EPIC</span>
                  <span className="text-zinc-500 text-xs">Rare</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-blue-400 text-sm font-medium">RARE</span>
                  <span className="text-zinc-500 text-xs">Common</span>
                </div>
              </div>
            </div>

            {/* Ticket Info */}
            <div className="p-4 bg-violet-900/20 rounded-xl border border-violet-800/30">
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                <Ticket className="w-4 h-4 text-violet-400" />
                Earn Tickets
              </h4>
              <p className="text-zinc-400 text-sm">Every pull earns you Tickets! Use Tickets to redeem exclusive items in the shop.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
